import React from "react";
import { Collapse, List, Button, Avatar, Tag, Badge, theme, Space } from "antd";
import { EditOutlined, DeleteOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { IProduct } from "../../../models/product/response";
import { QuantityInputMethod as QuantityInputMethodEnum } from "../../../models/order-product/types";
import {
    accountingToByUnit,
    formatQuantityByUnit,
    quantityInputToByUnit,
} from "../../../utils/helpers/quantity";

const { Panel } = Collapse;

export interface SelectedProduct {
    productId: number;
    product: IProduct;
    quantityData: {
        method: QuantityInputMethodEnum;
        count?: number;
        unitPartitionId?: number;
        availableWeight?: number;
    };
}

interface Props {
    selectedProducts: SelectedProduct[];
    onEdit: (selectedProduct: SelectedProduct) => void;
    onRemove: (productId: number) => void;
    storeId?: number;
}

const SelectedProductsList: React.FC<Props> = ({
    selectedProducts,
    onEdit,
    onRemove,
    storeId,
}) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();

    const getQuantityMeta = (product: IProduct) => {
        const storeQty = product.storeQuantities?.find((sq) => sq.store_id === storeId);
        const factor =
            product.unit?.factorAccountingSystem ||
            product.unit?.factor_accounting_system ||
            storeQty?.factorAccountingSystem ||
            storeQty?.factor_accounting_system ||
            1;

        return {
            factor,
            decimalPlaces: storeQty?.decimalPlaces ?? product.unit?.decimalPlaces,
        };
    };

    const getQuantityDisplay = (selectedProduct: SelectedProduct): string => {
        const { quantityData, product } = selectedProduct;
        const quantityByUnit = quantityInputToByUnit(quantityData, product);
        const { decimalPlaces, factor } = getQuantityMeta(product);

        return `${formatQuantityByUnit(
            quantityByUnit,
            decimalPlaces,
            factor,
        )} ${product.unit?.name || t("units")}`;
    };

    const calculateDiscountedPrice = (product: IProduct): number => {
        const { price, valueGeneralDiscount = 0, valueSpecialDiscount = 0 } = product;
        const totalDiscount = valueGeneralDiscount + valueSpecialDiscount;
        return price - (price * totalDiscount) / 100;
    };

    const getStoreAvailability = (product: IProduct): { available: boolean; quantity: number } => {
        if (!storeId || !product.storeQuantities) {
            return { available: false, quantity: 0 };
        }

        const storeQty = product.storeQuantities.find((sq) => sq.store_id === storeId);
        const factor =
            product.unit?.factorAccountingSystem ||
            product.unit?.factor_accounting_system ||
            storeQty?.factorAccountingSystem ||
            storeQty?.factor_accounting_system;
        const actualQuantity = storeQty
            ? storeQty.availableQuantityByUnit ??
            (factor ? accountingToByUnit(storeQty.quantity, factor) : undefined) ??
            0
            : 0;

        return {
            available: storeQty?.available || false,
            quantity: actualQuantity,
        };
    };

    if (selectedProducts.length === 0) {
        return null;
    }

    return (
        <Collapse
            defaultActiveKey={["selected-products"]}
            style={{
                marginBottom: 24,
                borderRadius: token.borderRadiusLG,
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
        >
            <Panel
                header={
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        fontSize: 16,
                        fontWeight: 600,
                    }}>
                        <ShoppingCartOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                        <span>{t("selected_products")}</span>
                        <Badge
                            count={selectedProducts.length}
                            style={{
                                backgroundColor: token.colorSuccess,
                                fontWeight: 600,
                            }}
                        />
                    </div>
                }
                key="selected-products"
            >
                <List
                    dataSource={selectedProducts}
                    renderItem={(selectedProduct) => {
                        const discountedPrice = calculateDiscountedPrice(selectedProduct.product);
                        const hasDiscount = (selectedProduct.product.valueGeneralDiscount || 0) + (selectedProduct.product.valueSpecialDiscount || 0) > 0;
                        const { available, quantity } = getStoreAvailability(selectedProduct.product);
                        const quantityMeta = getQuantityMeta(selectedProduct.product);

                        return (
                            <List.Item
                                key={selectedProduct.productId}
                                style={{
                                    padding: "16px",
                                    backgroundColor: token.colorBgContainer,
                                    borderRadius: token.borderRadius,
                                    marginBottom: 8,
                                    border: `1px solid ${token.colorBorder}`,
                                }}
                                actions={[
                                    <Button
                                        key="edit"
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => onEdit(selectedProduct)}
                                    >
                                        {t("edit")}
                                    </Button>,
                                    <Button
                                        key="remove"
                                        type="link"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => onRemove(selectedProduct.productId)}
                                    >
                                        {t("delete")}
                                    </Button>,
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar
                                            src={selectedProduct.product.image?.url}
                                            size={64}
                                            shape="square"
                                            style={{ border: `2px solid ${token.colorBorder}` }}
                                        >
                                            {!selectedProduct.product.image?.url &&
                                                selectedProduct.product.name.charAt(0)}
                                        </Avatar>
                                    }
                                    title={
                                        <div style={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: token.colorText,
                                            marginBottom: 4,
                                        }}>
                                            {selectedProduct.product.name}
                                        </div>
                                    }
                                    description={
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            {selectedProduct.product.unit && (
                                                <div style={{ fontSize: 13, color: token.colorTextSecondary }}>
                                                    {t("unit")}: {selectedProduct.product.unit.name}
                                                </div>
                                            )}

                                            {/* Price Display */}
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                {hasDiscount ? (
                                                    <>
                                                        <span style={{ fontSize: 13, textDecoration: "line-through", color: token.colorTextTertiary }}>
                                                            {selectedProduct.product.price?.toLocaleString()} {t("currency")}
                                                        </span>
                                                        <span style={{ fontSize: 14, fontWeight: 600, color: token.colorSuccess }}>
                                                            {discountedPrice.toLocaleString()} {t("currency")}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span style={{ fontSize: 14, fontWeight: 600, color: token.colorText }}>
                                                        {selectedProduct.product.price?.toLocaleString()} {t("currency")}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Availability Display */}
                                            {storeId && (
                                                <div>
                                                    <Tag
                                                        color={available ? "success" : "error"}
                                                        style={{ fontSize: 12, marginRight: 0 }}
                                                    >
                                                        {available
                                                            ? `${t("available")}: ${formatQuantityByUnit(
                                                                quantity,
                                                                quantityMeta.decimalPlaces,
                                                                quantityMeta.factor,
                                                            )} ${selectedProduct.product.unit?.name || ""}`
                                                            : t("unavailable")}
                                                    </Tag>
                                                </div>
                                            )}

                                            <div>
                                                <Tag
                                                    color="blue"
                                                    style={{
                                                        fontSize: 14,
                                                        padding: "4px 12px",
                                                        fontWeight: 500,
                                                        marginTop: 4
                                                    }}
                                                >
                                                    {t("quantity")}: {getQuantityDisplay(selectedProduct)}
                                                </Tag>
                                            </div>
                                        </Space>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            </Panel>
        </Collapse>
    );
};

export default SelectedProductsList;
